/**
 * Fitness plan API — fitplus-platform with legacy Cloud Functions fallback.
 *
 * Routing rule: use FitPlus only after migration is confirmed for this browser
 * session. ensureMigrated() syncs from the server status endpoint first so
 * already-migrated users switch without a re-login.
 */

import { slimActivity } from '@/utils/slimActivity';

const LEGACY_BASE = 'https://europe-west1-sportler-plus.cloudfunctions.net';
const MIGRATION_KEY_PREFIX = 'fitnessplan_migration_';
const READY_STATUSES = new Set(['migrated', 'already_migrated', 'partial_success']);

/** @type {Map<string, Promise<{status: string}>>} */
const migrateInFlight = new Map();

function fitplusBase() {
	return (process.env.VUE_APP_FITPLUS_PLATFORM_API_BASE || 'http://localhost:9050').replace(/\/$/, '');
}

function fitplusEnabled() {
	return process.env.VUE_APP_FITNESSPLAN_USE_FITPLUS === 'true';
}

function migrationCacheKey(userId) {
	return `${MIGRATION_KEY_PREFIX}${userId}`;
}

export function getCachedMigrationStatus(userId) {
	try {
		return window.sessionStorage.getItem(migrationCacheKey(userId));
	} catch (e) {
		return null;
	}
}

function setCachedMigrationStatus(userId, status) {
	try {
		window.sessionStorage.setItem(migrationCacheKey(userId), status);
	} catch (e) {
		/* ignore */
	}
}

function isReadyStatus(status) {
	return READY_STATUSES.has(status);
}

export function shouldUseFitplus(userId) {
	if (!fitplusEnabled() || !userId) {
		return false;
	}
	return isReadyStatus(getCachedMigrationStatus(userId));
}

async function legacyFetch(path, init) {
	const url = `${LEGACY_BASE}/${path}`;
	const res = await fetch(url, init);
	return res;
}

async function fitplusFetch(path, init) {
	const url = `${fitplusBase()}/api/v1/fitnessplan/${path.replace(/^\//, '')}`;
	const res = await fetch(url, init);
	return res;
}

/**
 * Ensure this user is migrated and the session cache is set before FitPlus calls.
 * Dedupes concurrent callers per userId.
 */
export async function ensureMigrated(userId) {
	if (!fitplusEnabled() || !userId) {
		return { status: 'disabled' };
	}

	const cached = getCachedMigrationStatus(userId);
	if (isReadyStatus(cached)) {
		return { status: cached };
	}

	if (migrateInFlight.has(userId)) {
		return migrateInFlight.get(userId);
	}

	const run = (async () => {
		try {
			// Cheap path: already migrated on server (e.g. admin/manual migrate).
			const statusRes = await fetch(
				`${fitplusBase()}/api/v1/fitnessplan/migrate/status?userId=${encodeURIComponent(userId)}`,
			);
			if (statusRes.ok) {
				const statusData = await statusRes.json().catch(() => null);
				if (statusData && statusData.status === 'migrated') {
					setCachedMigrationStatus(userId, 'already_migrated');
					return { status: 'already_migrated', ...statusData };
				}
			}

			const res = await fetch(`${fitplusBase()}/api/v1/fitnessplan/migrate`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ userId }),
			});
			const data = await res.json().catch(() => null);
			if (!res.ok || !data) {
				console.warn('Fitness plan migration deferred', res.status, data);
				return { status: 'failed', errors: [`HTTP ${res.status}`] };
			}
			if (data.status) {
				setCachedMigrationStatus(userId, data.status);
			}
			if (data.status === 'failed') {
				console.warn('Fitness plan migration deferred', data);
			}
			return data;
		} catch (error) {
			console.warn('Fitness plan migration request failed', error);
			return { status: 'failed', errors: [String(error)] };
		} finally {
			migrateInFlight.delete(userId);
		}
	})();

	migrateInFlight.set(userId, run);
	return run;
}

async function useFitplusAfterMigrate(userId) {
	await ensureMigrated(userId);
	return shouldUseFitplus(userId);
}

async function apiFetch(userId, legacyPath, fitplusPath, init) {
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch(fitplusPath, init)
		: await legacyFetch(legacyPath, init);
	return res;
}

export async function getUpcomingEvents(userId, limit = 5) {
	const res = await apiFetch(
		userId,
		`upcomingEventsEU?userId=${encodeURIComponent(userId)}&limit=${limit}`,
		`upcoming?userId=${encodeURIComponent(userId)}&limit=${limit}`,
	);
	if (!res.ok) {
		throw new Error(`upcomingEvents failed: ${res.status}`);
	}
	return res.json();
}

export async function getCalendar(userId, params = {}) {
	const qs = new URLSearchParams({ userId, ...params });
	let useFitplus = await useFitplusAfterMigrate(userId);
	let res = useFitplus
		? await fitplusFetch(`calendar?${qs}`)
		: await legacyFetch(`getFitnessplanCalendar?${qs}`);

	// Stale session cache: FitPlus 404 ("User not migrated") → clear cache, try legacy
	if (useFitplus && res.status === 404) {
		try {
			window.sessionStorage.removeItem(migrationCacheKey(userId));
		} catch (e) {
			/* ignore */
		}
		res = await legacyFetch(`getFitnessplanCalendar?${qs}`);
	}

	// No plan yet is a valid empty calendar (legacy + FitPlus both use 404 for this)
	if (res.status === 404) {
		return { success: true, events: [], totalEvents: 0 };
	}
	if (!res.ok) {
		throw new Error(`getFitnessplanCalendar failed: ${res.status}`);
	}
	return res.json();
}

export async function getFitnessPlanEvent(userId, { guid, activityDate, activityTime }) {
	const qs = new URLSearchParams({ userId, guid, activityDate, activityTime });
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch(`events/detail?${qs}`)
		: await legacyFetch(`getFitnessPlanEvent?${qs}`);
	if (!res.ok) {
		throw new Error(`getFitnessPlanEvent failed: ${res.status}`);
	}
	return res.json();
}

export async function getStatistics(userId, { scope = 'entire', startDate } = {}) {
	const qs = new URLSearchParams({ userId, scope });
	if (startDate) {
		qs.set('startDate', startDate);
	}
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch(`statistics?${qs}`)
		: await legacyFetch(`getFitnessplanStatistics?${qs}`);
	if (!res.ok) {
		throw new Error(`getFitnessplanStatistics failed: ${res.status}`);
	}
	return res.json();
}

export async function checkChallengeStatus(userId) {
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch(`challenges/status?userId=${encodeURIComponent(userId)}`)
		: await legacyFetch(`checkChallengeStatus?userId=${encodeURIComponent(userId)}`);
	if (!res.ok) {
		throw new Error(`checkChallengeStatus failed: ${res.status}`);
	}
	return res.json();
}

export async function addFitnessPlanEvent(userId, eventData) {
	const body = JSON.stringify({
		userId,
		eventData: {
			...eventData,
			activity: slimActivity(eventData.activity),
		},
	});
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch('events', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body })
		: await legacyFetch('addFitnessPlanEvent', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
	if (!res.ok) {
		throw new Error(`addFitnessPlanEvent failed: ${res.status}`);
	}
	return res.json();
}

export async function updateFitnessPlanEvent(userId, eventId, eventData) {
	const body = JSON.stringify({
		userId,
		eventId,
		eventData: {
			...eventData,
			activity: slimActivity(eventData.activity),
		},
	});
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch(`events/${encodeURIComponent(eventId)}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body,
		})
		: await legacyFetch('updateFitnessPlanEvent', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		});
	if (!res.ok) {
		throw new Error(`updateFitnessPlanEvent failed: ${res.status}`);
	}
	return res.json();
}

export async function addEventException(userId, eventId, exceptionDate) {
	const body = JSON.stringify({ userId, eventId, exceptionDate });
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch(`events/${encodeURIComponent(eventId)}/exception`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		})
		: await legacyFetch('addEventException', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		});
	if (!res.ok) {
		throw new Error(`addEventException failed: ${res.status}`);
	}
	return res.json();
}

export async function updateEventStatus(userId, eventInfo, activityStatus) {
	const body = JSON.stringify({ userId, eventInfo, activityStatus });
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch('events/status', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		})
		: await legacyFetch('updateEventStatus', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		});
	if (!res.ok) {
		throw new Error(`updateEventStatus failed: ${res.status}`);
	}
	return res.json();
}

export async function deleteFitnessPlanEvent(userId, eventInfo, eventId) {
	const body = JSON.stringify({ userId, eventInfo, eventId });
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch('events/delete', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		})
		: await legacyFetch('deleteFitnessPlanEvent', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		});
	if (!res.ok) {
		throw new Error(`deleteFitnessPlanEvent failed: ${res.status}`);
	}
	return res.json();
}

export async function deleteFitnessPlanChallenge(userId, challengeId) {
	const body = JSON.stringify({ userId, challengeId });
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch(`challenges/${encodeURIComponent(challengeId)}?userId=${encodeURIComponent(userId)}`, {
			method: 'DELETE',
		})
		: await legacyFetch('deleteFitnessPlanChallenge', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		});
	if (!res.ok) {
		throw new Error(`deleteFitnessPlanChallenge failed: ${res.status}`);
	}
	return res.json();
}

export async function startFitnessPlanChallenge(userId, challengeData) {
	const body = JSON.stringify({ userId, challengeData });
	const useFitplus = await useFitplusAfterMigrate(userId);
	const res = useFitplus
		? await fitplusFetch('challenges/start', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		})
		: await legacyFetch('startFitnessPlanChallenge', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		});
	if (!res.ok) {
		throw new Error(`startFitnessPlanChallenge failed: ${res.status}`);
	}
	return res.json();
}

export function webcalSubscribeUrl(userId) {
	if (shouldUseFitplus(userId)) {
		return `webcal://${fitplusBase().replace(/^https?:\/\//, '')}/api/v1/fitnessplan/calendar.ics?userId=${encodeURIComponent(userId)}`;
	}
	return `webcal://europe-west1-sportler-plus.cloudfunctions.net/userCalendarEU?userId=${encodeURIComponent(userId)}`;
}

export { slimActivity };
